"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import DashboardSidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import type { Portal } from "@/lib/portals";
import type { NavSection } from "@/lib/types";

type AppShellProps = {
  navSections: NavSection[];
  currentPortal?: Portal;
  userName?: string | null;
  children: React.ReactNode;
};

export default function AppShell({
  navSections,
  currentPortal = "staff",
  userName,
  children,
}: AppShellProps) {
  const pathname = usePathname();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Scrolling happens in this inner container, not the window, so Next.js
  // does not reset it on navigation — without this, every page loads at the
  // previous page's scroll position.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileNavOpen(false);
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileNavOpen]);

  return (
    <div className="flex min-h-screen bg-canvas">
      <div className="hidden md:block">
        <DashboardSidebar navSections={navSections} userName={userName} />
      </div>
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-50 flex md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Application navigation"
        >
          <DashboardSidebar
            navSections={navSections}
            userName={userName}
            onNavigate={() => setMobileNavOpen(false)}
          />
          <button
            type="button"
            className="flex-1 bg-black/40"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          currentPortal={currentPortal}
          mobileNavOpen={mobileNavOpen}
          onMobileNavToggle={() => setMobileNavOpen((open) => !open)}
        />
        <div ref={scrollRef} className="flex-1 overflow-auto p-4 sm:p-6 lg:p-9">
          {children}
        </div>
      </div>
    </div>
  );
}
